import { Head, useForm, usePage } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import AuthLayout from "@/layouts/auth-layout"
import { cn } from "@/lib/utils"
import { legalPartnerTermsPath, legalPrivacyPath } from "@/routes"

export default function PartnerInvitation() {
  const { invitation_token: token, partner_name } = usePage<{
    invitation_token: string
    partner_name?: string
  }>().props
  const partnerName = partner_name ?? "your team"

  const { data, setData, patch, processing, errors } = useForm({
    partner_user: {
      name: "",
      password: "",
      password_confirmation: "",
      invitation_token: token,
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch("/partner_users/invitation")
  }

  return (
    <AuthLayout title="" description="">
      <Head title="Accept Invitation" />
      <div className={cn("flex w-full flex-col gap-6")}>
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-xl">Welcome to Imperative</CardTitle>
            <CardDescription>
              Create an account to join {partnerName}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="name">Name</FieldLabel>
                  <Input
                    id="name"
                    type="text"
                    name="partner_user[name]"
                    placeholder="Enter your name"
                    value={data.partner_user.name}
                    onChange={(e) =>
                      setData("partner_user", {
                        ...data.partner_user,
                        name: e.target.value,
                      })
                    }
                    required
                    autoFocus
                  />
                  {errors["partner_user.name"] && (
                    <FieldError>{errors["partner_user.name"]}</FieldError>
                  )}
                </Field>
                <Field>
                  <FieldLabel htmlFor="password">Password</FieldLabel>
                  <Input
                    id="password"
                    type="password"
                    name="partner_user[password]"
                    placeholder="Enter your password"
                    value={data.partner_user.password}
                    onChange={(e) =>
                      setData("partner_user", {
                        ...data.partner_user,
                        password: e.target.value,
                      })
                    }
                    required
                  />
                  {errors["partner_user.password"] && (
                    <FieldError>{errors["partner_user.password"]}</FieldError>
                  )}
                </Field>
                <Field>
                  <FieldLabel htmlFor="password_confirmation">
                    Password Confirmation
                  </FieldLabel>
                  <Input
                    id="password_confirmation"
                    type="password"
                    name="partner_user[password_confirmation]"
                    placeholder="Confirm your password"
                    value={data.partner_user.password_confirmation}
                    onChange={(e) =>
                      setData("partner_user", {
                        ...data.partner_user,
                        password_confirmation: e.target.value,
                      })
                    }
                    required
                  />
                  {errors["partner_user.password_confirmation"] && (
                    <FieldError>
                      {errors["partner_user.password_confirmation"]}
                    </FieldError>
                  )}
                </Field>
                <Field>
                  <Button
                    type="submit"
                    className="flex w-full items-center justify-center"
                    loading={processing}
                  >
                    {processing ? "Setting up..." : "Continue"}
                  </Button>
                  {/* IMP-284: record agreement to the partner legal terms at sign-up */}
                  <p className="text-muted-foreground text-center text-xs text-balance">
                    By continuing, you agree to the{" "}
                    <a
                      href={legalPartnerTermsPath()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2"
                    >
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a
                      href={legalPrivacyPath()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </Field>
              </FieldGroup>
            </form>
          </CardContent>
        </Card>
      </div>
    </AuthLayout>
  )
}
